// src/components/admin/ManageEditors.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  User,
  Globe,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Edit,
  Trash2,
  Eye,
  Plus,
  Search,
  X,
  Save,
  Upload,
  AlertTriangle,
  AlertCircle,
  Home,
  ChevronRight,
  LogOut,
  BookOpen,
  Award,
  Users,
  Filter,
  Building,
  Loader
} from 'lucide-react';
import axios from '../../utils/axios';
import { useAuth } from '../../context/AuthContext';
import Unauthorized from './UnauthorizedAccess';
import { EIC_ADMIN_ROLES } from './utils/AllowedRoles';
import { showToast } from '../../utils/toast';

// Quill modules configuration
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['link'],
    ['clean']
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent',
  'align',
  'link'
];

// Function to parse any content format to HTML for display
const parseToHTML = (content) => {
  if (!content) return '';

  // If it's already HTML (contains tags), return as is
  if (typeof content === 'string' && /<[a-z][\s\S]*>/i.test(content)) {
    return content;
  }

  // Check if it looks like a JSON string (starts with [ or {)
  if (typeof content === 'string' && (content.trim().startsWith('[') || content.trim().startsWith('{'))) {
    try {
      const parsed = JSON.parse(content);
      
      // Check if it's a Quill Delta array format [{"insert": "text"}]
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].insert !== undefined) {
        // Convert array format to Delta object
        return quillDeltaToHTML({ ops: parsed });
      }
      
      // Check if it's a Quill Delta object {ops: [...]}
      if (parsed.ops && Array.isArray(parsed.ops)) {
        return quillDeltaToHTML(parsed);
      }
      
      // If it's another type of array/object, stringify it
      return JSON.stringify(parsed);
    } catch (e) {
      // Not valid JSON, continue with other checks
      console.log('Not valid JSON:', e);
    }
  }

  // Try to parse as JSON object (if it's already an object)
  if (typeof content === 'object' && content !== null) {
    if (content.ops && Array.isArray(content.ops)) {
      return quillDeltaToHTML(content);
    }
    if (Array.isArray(content) && content.length > 0 && content[0].insert !== undefined) {
      return quillDeltaToHTML({ ops: content });
    }
  }

  // Handle plain text - split by newlines and wrap in paragraphs
  if (typeof content === 'string') {
    return content.split('\n').map(line => line.trim()).filter(line => line)
      .map(line => `<p>${line}</p>`).join('');
  }

  return typeof content === 'string' ? content : '';
};

// Convert Quill Delta to HTML
const quillDeltaToHTML = (delta) => {
  if (!delta || !delta.ops || !Array.isArray(delta.ops)) return '';

  let html = '';
  let listStack = [];
  let inParagraph = false;

  delta.ops.forEach(op => {
    let text = op.insert || '';
    
    // Handle newlines
    if (text === '\n') {
      // Close any open lists
      while (listStack.length > 0) {
        const listType = listStack.pop();
        html += `</${listType}>`;
      }
      // Close any open paragraph
      if (inParagraph) {
        html += '</p>';
        inParagraph = false;
      }
      return;
    }

    // Skip empty text
    if (!text.trim() && text !== '\n') return;

    // Handle text with attributes
    if (op.attributes) {
      // Handle lists
      if (op.attributes.list) {
        const listType = op.attributes.list === 'bullet' ? 'ul' : 'ol';
        
        // Close any open paragraph before starting list
        if (inParagraph) {
          html += '</p>';
          inParagraph = false;
        }
        
        // Open list if not already open
        if (listStack.length === 0 || listStack[listStack.length - 1] !== listType) {
          if (listStack.length > 0) {
            html += `</${listStack.pop()}>`;
          }
          html += `<${listType}>`;
          listStack.push(listType);
        }
        
        let listItem = text;
        if (op.attributes.bold) listItem = `<strong>${listItem}</strong>`;
        if (op.attributes.italic) listItem = `<em>${listItem}</em>`;
        if (op.attributes.underline) listItem = `<u>${listItem}</u>`;
        if (op.attributes.link) listItem = `<a href="${op.attributes.link}" target="_blank" rel="noopener noreferrer">${listItem}</a>`;
        if (op.attributes.strike) listItem = `<s>${listItem}</s>`;
        
        html += `<li>${listItem}</li>`;
        return;
      }

      // Close any open lists before other content
      while (listStack.length > 0) {
        const listType = listStack.pop();
        html += `</${listType}>`;
      }

      // Handle headers
      if (op.attributes.header) {
        // Close any open paragraph
        if (inParagraph) {
          html += '</p>';
          inParagraph = false;
        }
        
        const level = op.attributes.header;
        let headerText = text;
        if (op.attributes.bold) headerText = `<strong>${headerText}</strong>`;
        if (op.attributes.italic) headerText = `<em>${headerText}</em>`;
        if (op.attributes.underline) headerText = `<u>${headerText}</u>`;
        if (op.attributes.link) headerText = `<a href="${op.attributes.link}" target="_blank" rel="noopener noreferrer">${headerText}</a>`;
        if (op.attributes.strike) headerText = `<s>${headerText}</s>`;
        
        html += `<h${level}>${headerText}</h${level}>`;
        return;
      }

      // Handle alignment
      if (op.attributes.align) {
        // Close any open paragraph
        if (inParagraph) {
          html += '</p>';
          inParagraph = false;
        }
        
        let alignedText = text;
        if (op.attributes.bold) alignedText = `<strong>${alignedText}</strong>`;
        if (op.attributes.italic) alignedText = `<em>${alignedText}</em>`;
        if (op.attributes.underline) alignedText = `<u>${alignedText}</u>`;
        if (op.attributes.link) alignedText = `<a href="${op.attributes.link}" target="_blank" rel="noopener noreferrer">${alignedText}</a>`;
        if (op.attributes.strike) alignedText = `<s>${alignedText}</s>`;
        
        html += `<div style="text-align: ${op.attributes.align}">${alignedText}</div>`;
        return;
      }

      // Handle indent
      if (op.attributes.indent) {
        // Close any open paragraph
        if (inParagraph) {
          html += '</p>';
          inParagraph = false;
        }
        
        let indentedText = text;
        if (op.attributes.bold) indentedText = `<strong>${indentedText}</strong>`;
        if (op.attributes.italic) indentedText = `<em>${indentedText}</em>`;
        if (op.attributes.underline) indentedText = `<u>${indentedText}</u>`;
        if (op.attributes.link) indentedText = `<a href="${op.attributes.link}" target="_blank" rel="noopener noreferrer">${indentedText}</a>`;
        if (op.attributes.strike) indentedText = `<s>${indentedText}</s>`;
        
        html += `<div style="margin-left: ${op.attributes.indent * 20}px">${indentedText}</div>`;
        return;
      }

      // Handle regular text with formatting
      let formattedText = text;
      if (op.attributes.bold) formattedText = `<strong>${formattedText}</strong>`;
      if (op.attributes.italic) formattedText = `<em>${formattedText}</em>`;
      if (op.attributes.underline) formattedText = `<u>${formattedText}</u>`;
      if (op.attributes.link) formattedText = `<a href="${op.attributes.link}" target="_blank" rel="noopener noreferrer">${formattedText}</a>`;
      if (op.attributes.strike) formattedText = `<s>${formattedText}</s>`;
      
      // Open paragraph if not already open
      if (!inParagraph) {
        html += '<p>';
        inParagraph = true;
      }
      html += formattedText;
    } else {
      // Plain text
      if (text.trim()) {
        // Open paragraph if not already open
        if (!inParagraph) {
          html += '<p>';
          inParagraph = true;
        }
        html += text;
      }
    }
  });

  // Close any open paragraph
  if (inParagraph) {
    html += '</p>';
  }

  // Close any remaining lists
  while (listStack.length > 0) {
    const listType = listStack.pop();
    html += `</${listType}>`;
  }

  return html;
};

// Prepare content for Quill editor (convert any format to HTML that Quill can understand)
const prepareForQuill = (content) => {
  if (!content) return '';

  // If it's already HTML that Quill can handle, return as is
  if (typeof content === 'string' && /<[a-z][\s\S]*>/i.test(content)) {
    return content;
  }

  // Check if it looks like a JSON string (starts with [ or {)
  if (typeof content === 'string' && (content.trim().startsWith('[') || content.trim().startsWith('{'))) {
    try {
      const parsed = JSON.parse(content);
      
      // Check if it's a Quill Delta array format [{"insert": "text"}]
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].insert !== undefined) {
        return quillDeltaToHTML({ ops: parsed });
      }
      
      // Check if it's a Quill Delta object {ops: [...]}
      if (parsed.ops && Array.isArray(parsed.ops)) {
        return quillDeltaToHTML(parsed);
      }
    } catch (e) {
      console.log('Not valid JSON for Quill:', e);
    }
  }

  // Try to parse as JSON object (if it's already an object)
  if (typeof content === 'object' && content !== null) {
    if (content.ops && Array.isArray(content.ops)) {
      return quillDeltaToHTML(content);
    }
    if (Array.isArray(content) && content.length > 0 && content[0].insert !== undefined) {
      return quillDeltaToHTML({ ops: content });
    }
  }

  // Handle plain text
  if (typeof content === 'string') {
    return content.split('\n').map(line => `<p>${line}</p>`).join('');
  }

  return typeof content === 'string' ? content : '';
};


// Function to count words in HTML content
const countWords = (html) => {
  if (!html) return 0;
  const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.split(' ').filter(word => word.length > 0).length;
};

// Helper function to get photo URL
const getPhotoUrl = (photo, isOldEditor) => {
  if (!photo) return '/assets/images/asfischolar.jpg';
  
  if (isOldEditor === 'yes') {
    return `https://asfirj.org/useruploads/editors/${photo}`;
  } else {
    return `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:31000'}/useruploads/editors/${photo}`;
  }
};

// Editor Card Component
const EditorCard = ({ editor, onEdit, onDelete, onView }) => {
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const truncateBio = (bio, maxLength = 100) => {
    if (!bio) return '';
    const html = parseToHTML(bio);
    const text = html.replace(/<[^>]*>/g, ''); // Strip HTML tags
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const photoUrl = getPhotoUrl(editor.photo, editor.is_old_editor);

  return (
    <div className="editor-card bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
      <div className="card-content p-6">
        {/* Avatar Container */}
        <div className="relative mb-4">
          <div className="avatar-container w-24 h-24 mx-auto">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={editor.fullname}
                className="w-full h-full rounded-full object-cover border-4 border-gray-100"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/assets/images/asfischolar.jpg';
                }}
              />
            ) : (
              <div className="w-full h-full rounded-full bg-gradient-to-br from-[--asfi-purple] to-[--asfi-blue] flex items-center justify-center text-white text-2xl font-bold border-4 border-gray-100">
                {getInitials(editor.fullname)}
              </div>
            )}
          </div>

 
        </div>

        {/* Editor Info */}
        <h3 className="text-lg font-semibold text-gray-900 text-center">
          {editor.prefix} {editor.fullname}
        </h3>
        
        <p className="text-sm text-[--asfi-purple] font-medium text-center mb-3">
          {editor.field}
        </p>

        {/* Discipline */}
        {editor.discipline && (
          <p className="text-sm text-gray-600 flex items-center justify-center mb-2">
            <Building className="w-4 h-4 mr-1 text-gray-400" />
            <span className="truncate">{editor.discipline}</span>
          </p>
        )}

        {/* Country */}
        {editor.country && (
          <p className="text-sm text-gray-600 flex items-center justify-center mb-3">
            <Globe className="w-4 h-4 mr-1 text-gray-400" />
            {editor.country}
          </p>
        )}

        {/* Bio Preview */}
        <p className="text-sm text-gray-500 text-center mb-4 line-clamp-2">
          {truncateBio(editor.bio)}
        </p>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => onView(editor)}
            className="flex-1 py-2 px-4 bg-gray-50 text-gray-700 rounded-lg hover:bg-[--asfi-purple] hover:text-white transition-colors flex items-center justify-center gap-2 text-sm font-medium"
          >
            <Eye className="w-4 h-4" />
            View
          </button>
          <button
            onClick={() => onEdit(editor)}
            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
            title="Edit Editor"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(editor)}
            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
            title="Delete Editor"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Editor Modal Component
const EditorModal = ({ isOpen, onClose, onSubmit, editor, mode = 'create' }) => {
  const { user } = useAuth();
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    prefix: '',
    fullname: '',
    field: '',
    discipline: '',
    country: '',
    email: '',
    bio: '',
    id: '', // For editing existing editor
    photoUrl: null, // URL of the uploaded photo
    is_old_editor: 'no'
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [bioWordCount, setBioWordCount] = useState(0);
  const [fields, setFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);

  // Fetch available fields
  useEffect(() => {
    const fetchFields = async () => {
      try {
        setLoadingFields(true);
        const response = await axios.get('/api/fields');
        if (response.data.status === 'success') {
          setFields(response.data.fields);
        }
      } catch (error) {
        console.error('Error fetching fields:', error);
      } finally {
        setLoadingFields(false);
      }
    };

    if (isOpen) {
      fetchFields();
    }
  }, [isOpen]);

  // Load editor data when editing
  useEffect(() => {
    if (editor && mode === 'edit') {
      // Prepare bio for Quill editor
      const preparedBio = prepareForQuill(editor.bio || '');
      
      setFormData({
        prefix: editor.prefix || '',
        fullname: editor.fullname || '',
        field: editor.field || '',
        discipline: editor.discipline || '',
        country: editor.country || '',
        email: editor.email || '',
        bio: preparedBio,
        id: editor.id || '',
        photoUrl: editor.photo || null, // Store existing photo filename
        is_old_editor: editor.is_old_editor || 'no'
      });
      setPhotoPreview(getPhotoUrl(editor.photo, editor.is_old_editor));
      
      // Update word count for existing bio
      if (preparedBio) {
        const wordCount = countWords(preparedBio);
        setBioWordCount(wordCount);
      }
    } else {
      // Reset form for new editor
      setFormData({
        prefix: '',
        fullname: '',
        field: '',
        discipline: '',
        country: '',
        email: '',
        bio: '',
        id: '',
        photoUrl: null,
        is_old_editor: 'no'
      });
      setPhotoPreview(null);
      setBioWordCount(0);
    }
    setFormErrors({});
  }, [editor, mode, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBioChange = (content, delta, source, editor) => {
    setFormData(prev => ({ ...prev, bio: content }));
    
    // Update word count
    const text = editor.getText();
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    setBioWordCount(words.length);
    
    if (formErrors.bio) {
      setFormErrors(prev => ({ ...prev, bio: '' }));
    }
  };

  // Handle file selection and immediate upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Please upload a valid image file (JPEG, PNG, GIF, WEBP, BMP)', 'error');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file size (10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      showToast('Image size exceeds 10MB', 'error');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload the file immediately
    setUploadingPhoto(true);
    setFormErrors(prev => ({ ...prev, photo: '' }));

    try {
      const uploadData = new FormData();
      uploadData.append('image', file);
      uploadData.append('id', editor?.id)

      const response = await axios.post('/journal/public/editor-profile-image', uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.status === 'success' && response.data.url) {
        // Extract filename from URL
        const urlParts = response.data.url.split('/');
        const filename = urlParts[urlParts.length - 1];
        
        setFormData(prev => ({ 
          ...prev, 
          photoUrl: filename,
          is_old_editor: 'no' // New uploads are not old editors
        }));
        
        showToast('Photo uploaded successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      const message = error.response?.data?.message || error.message || 'Failed to upload photo';
      showToast(message, 'error');
      
      // Reset preview on error
      setPhotoPreview(getPhotoUrl(editor?.photo, editor?.is_old_editor) || null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Remove uploaded photo
  const handleRemovePhoto = () => {
    setFormData(prev => ({ 
      ...prev, 
      photoUrl: null,
      is_old_editor: 'no'
    }));
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.fullname.trim()) {
      errors.fullname = 'Name is required';
    }
    
    if (!formData.field.trim()) {
      errors.field = 'Field/Role is required';
    }
    
    // Require photo
    if (!formData.photoUrl) {
      errors.photo = 'Profile picture is required';
    }
    
    if (!formData.bio || formData.bio === '<p><br></p>' || formData.bio.trim() === '') {
      errors.bio = 'Biography is required';
    } else if (bioWordCount < 20) {
      errors.bio = `Biography must be at least 20 words (current: ${bioWordCount})`;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check if form is ready to save
  const isFormReady = () => {
    return formData.photoUrl && !uploadingPhoto;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaveLoading(true);
    
    try {
      // Prepare FormData (no file, just the photo URL/filename)
      const submitData = new FormData();
      submitData.append('prefix', formData.prefix);
      submitData.append('fullname', formData.fullname);
      submitData.append('email', formData.email);
      submitData.append('field', formData.field);
      submitData.append('discipline', formData.discipline);
      submitData.append('country', formData.country);
      
      // Submit bio as HTML from Quill
      submitData.append('bio', formData.bio);
      
      // Submit photo filename (already uploaded)
      if (formData.photoUrl) {
        submitData.append('photo', formData.photoUrl);
      }
      
      submitData.append('is_old_editor', formData.is_old_editor);
      
      if (mode === 'edit' && editor?.id) {
        submitData.append('id', editor.id);
      }

      await onSubmit(submitData, mode);
      onClose();
    } catch (error) {
      console.error('Error saving editor:', error);
      setFormErrors(prev => ({
        ...prev,
        submit: error.message || 'Failed to save editor'
      }));
    } finally {
      setSaveLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <User className="w-5 h-5 mr-2 text-[--asfi-purple]" />
                {mode === 'create' ? 'Add New Editor' : 'Update Editor Details'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error Message */}
          {formErrors.submit && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="text-sm">{formErrors.submit}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                <User className="w-4 h-4 mr-2 text-[--asfi-purple]" />
                Basic Information
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Prefix */}
                <div>
                  <label htmlFor="prefix" className="block text-sm font-medium text-gray-700 mb-1">
                    Prefix (e.g., Prof., Dr., Mrs.)
                  </label>
                  <input
                    type="text"
                    id="prefix"
                    name="prefix"
                    value={formData.prefix}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                    placeholder="Enter prefix..."
                  />
                </div>

                {/* Name */}
                <div>
                  <label htmlFor="fullname" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="fullname"
                    name="fullname"
                    value={formData.fullname}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent ${
                      formErrors.fullname ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter full name..."
                  />
                  {formErrors.fullname && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.fullname}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                    placeholder="editor@example.com..."
                  />
                </div>

                {/* Field/Role */}
                <div>
                  <label htmlFor="field" className="block text-sm font-medium text-gray-700 mb-1">
                    Field/Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="field"
                    name="field"
                    value={formData.field}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent ${
                      formErrors.field ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loadingFields}
                  >
                    <option value="">Select Field/Role</option>
                    {fields.map((field, index) => (
                      <option key={index} value={field.field}>
                        {field.field}
                      </option>
                    ))}
                  </select>
                  {formErrors.field && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.field}</p>
                  )}
                </div>

                {/* Discipline */}
                <div>
                  <label htmlFor="discipline" className="block text-sm font-medium text-gray-700 mb-1">
                    Discipline
                  </label>
                  <input
                    type="text"
                    id="discipline"
                    name="discipline"
                    value={formData.discipline}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                    placeholder="e.g., Epidemiology, Economics, Statistics..."
                  />
                </div>

                {/* Country */}
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                    placeholder="Enter country..."
                  />
                </div>
              </div>
            </div>

            {/* Avatar Upload */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                <Upload className="w-4 h-4 mr-2 text-[--asfi-purple]" />
                Profile Picture <span className="text-red-500 ml-1">*</span>
              </h4>
              
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-12 h-12 text-gray-400" />
                    )}
                  </div>
                  {uploadingPhoto && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                      <Loader className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={uploadingPhoto}
                  />
                  
                  {formData.photoUrl ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-800">Photo uploaded</p>
                          <p className="text-xs text-green-600 truncate">{formData.photoUrl}</p>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemovePhoto}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove photo"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-sm text-[--asfi-purple] hover:text-[--asfi-blue] font-medium"
                      >
                        Change photo
                      </button>
                    </div>
                  ) : (
                    <div>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        className="px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {uploadingPhoto ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload Photo
                          </>
                        )}
                      </button>
                      <p className="mt-2 text-xs text-gray-500">
                        JPG, PNG, GIF, WEBP, BMP up to 10MB
                      </p>
                    </div>
                  )}
                  
                  {formErrors.photo && (
                    <p className="mt-2 text-xs text-red-600">{formErrors.photo}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Biography with ReactQuill */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                <BookOpen className="w-4 h-4 mr-2 text-[--asfi-purple]" />
                Biography <span className="text-red-500 ml-1">*</span>
              </h4>
              
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={formData.bio}
                  onChange={handleBioChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter editor's biography..."
                  style={{ height: '200px', marginBottom: '50px' }}
                />
              </div>
              
              {formErrors.bio && (
                <p className="mt-1 text-xs text-red-600">{formErrors.bio}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Minimum 20 words. Current: {bioWordCount} words
              </p>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saveLoading || uploadingPhoto || !isFormReady()}
                className="px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saveLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : uploadingPhoto ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Uploading photo...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {mode === 'create' ? 'Add Editor' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// View Profile Modal
const ViewProfileModal = ({ isOpen, onClose, editor, onEdit, onDelete }) => {
  if (!isOpen || !editor) return null;

  const photoUrl = getPhotoUrl(editor.photo, editor.is_old_editor);

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Parse the bio to HTML for display
  const parsedBio = parseToHTML(editor.bio);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Editor Profile</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Profile Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-6">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200">
                  {photoUrl ? (
                    <img src={photoUrl} alt={editor.fullname} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[--asfi-purple] to-[--asfi-blue] flex items-center justify-center text-white text-2xl font-bold">
                      {getInitials(editor.fullname)}
                    </div>
                  )}
                </div>
                
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {editor.prefix && <span className="text-[--asfi-purple] mr-1">{editor.prefix}</span>}
                    {editor.fullname}
                  </h2>
                  <p className="text-lg text-[--asfi-purple] font-medium">{editor.field}</p>
                </div>
              </div>
              
              {/* Edit and Delete buttons in modal */}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    onClose();
                    onEdit(editor);
                  }}
                  className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                  title="Edit Editor"
                >
                  <Edit className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    onClose();
                    onDelete(editor);
                  }}
                  className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                  title="Delete Editor"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
              {editor.discipline && (
                <div className="flex items-center text-gray-700">
                  <Building className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="text-sm">{editor.discipline}</span>
                </div>
              )}
              
              {editor.country && (
                <div className="flex items-center text-gray-700">
                  <Globe className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="text-sm">{editor.country}</span>
                </div>
              )}
            </div>

            {/* Biography */}
            {parsedBio && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Biography</h4>
                <div 
                  className="text-sm text-gray-600 prose max-w-none ql-editor"
                  dangerouslySetInnerHTML={{ __html: parsedBio }}
                />
              </div>
            )}
          </div>

          {/* Close Button */}
          <div className="mt-8 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const ManageEditors = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Check if user has required editorial level
  const isAuthorized = user && EIC_ADMIN_ROLES.includes(user.editorial_level || user.editorialLevel);

  if (!isAuthorized) {
    return (
      <Unauthorized
        message="You need editor-in-chief or admin privileges to access this feature."
      />
    );
  }
  // State
  const [editors, setEditors] = useState([]);
  const [groupedEditors, setGroupedEditors] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedEditor, setSelectedEditor] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [fields, setFields] = useState([]);

  // Fetch all fields
  const fetchFields = async () => {
    try {
      const response = await axios.get('/api/fields');
      if (response.data.status === 'success') {
        setFields(response.data.fields);
      }
    } catch (error) {
      console.error('Error fetching fields:', error);
    }
  };

  // Fetch editors for a specific field
  const fetchEditorsByField = async (field, offset = 0) => {
    try {
      const response = await axios.get(`/api/editors/by-field?field=${field}&offset=${offset}&limit=50`);
      if (response.data.status === 'success') {
        return response.data.editors;
      }
      return [];
    } catch (error) {
      console.error(`Error fetching editors for field ${field}:`, error);
      return [];
    }
  };

  // Fetch all editors
  const fetchAllEditors = async () => {
    try {
      setLoading(true);
      
      // First get all fields
      await fetchFields();
      
      // Then fetch editors for each field
      const fieldsResponse = await axios.get('/api/fields');
      if (fieldsResponse.data.status === 'success') {
        const fieldList = fieldsResponse.data.fields;
        const allEditors = [];
        const grouped = {};
        
        for (const field of fieldList) {
          const editors = await fetchEditorsByField(field.field);
          allEditors.push(...editors);
          grouped[field.field] = editors;
        }
        
        setEditors(allEditors);
        setGroupedEditors(grouped);
        
        // Initialize expanded sections
        const sections = {};
        fieldList.forEach(f => {
          sections[f.field] = true;
        });
        setExpandedSections(sections);
      }
    } catch (error) {
      console.error('Error fetching editors:', error);
      showToast(error.response?.data?.message || 'Failed to fetch editors', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load editors on mount
  useEffect(() => {
    fetchAllEditors();
  }, []);

  // Filter editors based on search
  const filteredGroupedEditors = useMemo(() => {
    if (!searchTerm.trim()) return groupedEditors;
    
    const searchLower = searchTerm.toLowerCase();
    const filtered = {};
    
    Object.entries(groupedEditors).forEach(([field, fieldEditors]) => {
      const matching = fieldEditors.filter(editor => 
        editor.fullname?.toLowerCase().includes(searchLower) ||
        editor.discipline?.toLowerCase().includes(searchLower) ||
        editor.country?.toLowerCase().includes(searchLower)
      );
      
      if (matching.length > 0) {
        filtered[field] = matching;
      }
    });
    
    return filtered;
  }, [groupedEditors, searchTerm]);

  const handleCreateClick = () => {
    setModalMode('create');
    setSelectedEditor(null);
    setShowModal(true);
  };

  const handleEditClick = (editor) => {
    setModalMode('edit');
    setSelectedEditor(editor);
    setShowModal(true);
  };

  const handleViewClick = (editor) => {
    setSelectedEditor(editor);
    setShowViewModal(true);
  };

  const handleDeleteClick = (editor) => {
    setDeleteConfirm(editor);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      const response = await axios.delete(`/api/editors/delete/${deleteConfirm.id}`);

      if (response.data.status === 'success') {
        await fetchAllEditors(); // Refresh the list
        showToast('Editor deleted successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Failed to delete editor');
      }
    } catch (error) {
      console.error('Error deleting editor:', error);
      showToast(error.response?.data?.message || 'Failed to delete editor', 'error');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleSubmitEditor = async (formData, mode) => {
    try {
      let response;
      
      if (mode === 'create') {
        response = await axios.post('/api/editors/add', formData, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
      } else {
        response = await axios.post('/api/editors/update', formData, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
      }

      if (response.data.status === 'success') {
        await fetchAllEditors(); // Refresh the list
        showToast(
          mode === 'create' 
            ? 'Editor added successfully' 
            : 'Editor updated successfully',
          'success'
        );
      } else {
        throw new Error(response.data.message || 'Failed to save editor');
      }
    } catch (error) {
      console.error('Error saving editor:', error);
      throw new Error(error.response?.data?.message || 'Failed to save editor');
    }
  };

  const toggleSection = (field) => {
    setExpandedSections(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleLoadMore = async (field, currentOffset) => {
    try {
      const moreEditors = await fetchEditorsByField(field, currentOffset);
      
      if (moreEditors.length > 0) {
        setGroupedEditors(prev => ({
          ...prev,
          [field]: [...(prev[field] || []), ...moreEditors]
        }));
        
        setEditors(prev => [...prev, ...moreEditors]);
      }
    } catch (error) {
      console.error('Error loading more editors:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Delete Editor
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Editor:</p>
                <p className="text-sm text-gray-900 mt-1">
                  {deleteConfirm.prefix} {deleteConfirm.fullname}
                </p>
                <p className="text-xs text-gray-500 mt-1">Field: {deleteConfirm.field}</p>
              </div>

              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 flex items-start">
                  <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Warning:</strong> This will permanently delete this editor's profile.
                  </span>
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content" id="mainContent">
        {/* Page Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="page-title text-3xl font-bold text-gray-900">Editors</h1>
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCreateClick}
                  className="px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Editor
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search editors by name, discipline, country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
            />
          </div>
        </div>

        {/* Editors Content */}
        <div id="editorsContent" className="px-6 pb-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[--asfi-purple]"></div>
            </div>
          ) : Object.keys(filteredGroupedEditors).length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No editors found</h3>
              <p className="text-gray-500">
                {searchTerm ? 'Try adjusting your search' : 'No editors have been added yet.'}
              </p>
              {!searchTerm && (
                <button
                  onClick={handleCreateClick}
                  className="mt-4 px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Your First Editor
                </button>
              )}
            </div>
          ) : (
            Object.entries(filteredGroupedEditors).map(([field, fieldEditors]) => (
              <div key={field} className="editor-section mb-8" id={`field-${field.toLowerCase().replace(/\s+/g, '-')}`}>
                {/* Section Header */}
                <div className="flex items-center justify-between mb-4">
                  <h2 className="section-title text-xl font-semibold text-gray-900">{field}</h2>
                  <button
                    onClick={() => toggleSection(field)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronRight className={`w-5 h-5 text-gray-500 transform transition-transform ${
                      expandedSections[field] ? 'rotate-90' : ''
                    }`} />
                  </button>
                </div>

                {/* Editors Grid */}
                {expandedSections[field] && (
                  <>
                    <div className="editors-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {fieldEditors.map((editor) => (
                        <EditorCard
                          key={editor.id}
                          editor={editor}
                          onEdit={handleEditClick}
                          onDelete={handleDeleteClick}
                          onView={handleViewClick}
                        />
                      ))}
                    </div>

                    {/* Load More Button - if needed */}
                    {fieldEditors.length >= 50 && (
                      <div className="load-more-container mt-4 text-center">
                        <button
                          onClick={() => handleLoadMore(field, fieldEditors.length)}
                          className="load-more-btn px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                        >
                          Load More Editors
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </main>

      {/* Modals */}
      <EditorModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmitEditor}
        editor={selectedEditor}
        mode={modalMode}
      />

      <ViewProfileModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        editor={selectedEditor}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
      />
    </div>
  );
};

export default ManageEditors;